import React, { ComponentType, ReactElement, useEffect, useMemo, useRef, useState } from "react";

import { stampCropSelectable } from "~/admin/setup/customer/use-crop-list";

const PROPS_CROP_NAME = "cropName";

interface ICropOption {
    activeYn: boolean;
    guid: string;
    isAgvanceLinked: boolean | null;
    [key: string]: unknown;
}

interface ICropRecordRow {
    cropGuid: string;
}

interface IRestrictedCropListProps {
    /** The underlying grid to render: crop-list.jsx (equation-list) or crop-info-list.jsx (agBytes). */
    component: ComponentType<any>;
    /** The crop-name picklist only -- the other picklists (cropClassName, cropPurpose,
     * growthStageOrder) pass through untouched via the rest of `options`/`picklistOptions`. */
    cropNameOptions: ICropOption[];
    formatMessage: (descriptor: unknown) => string;
    /** Fired whenever the unlinked state changes. Panels use it to block Save. */
    onUnlinkedCropsChange?: (hasUnlinkedCrop: boolean) => void;
    /** Which prop name the target grid expects its picklist bundle under. */
    optionsPropName: "options" | "picklistOptions";
    record: ICropRecordRow[];
    [passThrough: string]: unknown;
}

/**
 * Wraps a setup crop grid (crop-list.jsx or crop-info-list.jsx) with Agvance-linkage
 * visibility/marking: stamps isSelectable/getToolTip on every crop-name option (hiding is
 * cosmetic) and reports unlinked rows so the host can block Save. Nothing is filtered from
 * `cropNameOptions`, so a selected-but-unlinked crop still resolves.
 *
 * `cropNameOptions` is passed separately from `options`/`picklistOptions` so this wrapper is
 * the sole source of truth for `options.cropName`/`picklistOptions.cropName` — always
 * overwritten with the stamped version, so the two can't drift apart.
 */
const RestrictedCropList = ({
    component: GridComponent,
    cropNameOptions,
    formatMessage,
    onUnlinkedCropsChange,
    optionsPropName,
    record,
    ...passThroughProps
}: IRestrictedCropListProps): ReactElement => {
    const selectableCropOptions = useMemo(
        () => stampCropSelectable(cropNameOptions ?? [], formatMessage),
        [cropNameOptions, formatMessage]
    );

    const optionsByGuid = useMemo(
        () => new Map(selectableCropOptions.map((option) => [option.guid, option])),
        [selectableCropOptions]
    );

    // withEditableGrid mutates `record` in place without a re-render — currentRows keeps the
    // unlinked derivation live across add/delete.
    const [currentRows, setCurrentRows] = useState<ICropRecordRow[]>(record ?? []);
    useEffect(() => {
        setCurrentRows(record ?? []);
    }, [record]);

    const unlinkedCropGuids = useMemo(
        () =>
            new Set(
                (currentRows ?? [])
                    .map((row) => row.cropGuid)
                    .filter((guid) => guid && optionsByGuid.get(guid)?.isAgvanceLinked === false)
            ),
        [currentRows, optionsByGuid]
    );

    const hasUnlinkedCrop = unlinkedCropGuids.size > 0;

    // Ref, not a dependency — class hosts pass a fresh callback every render, which would
    // loop forever if the effect keyed on it.
    const onUnlinkedCropsChangeRef = useRef(onUnlinkedCropsChange);
    useEffect(() => {
        onUnlinkedCropsChangeRef.current = onUnlinkedCropsChange;
    });
    useEffect(() => {
        onUnlinkedCropsChangeRef.current?.(hasUnlinkedCrop);
    }, [hasUnlinkedCrop]);

    const onTextChange = (event: unknown, nextRecord: ICropRecordRow[]): void => {
        setCurrentRows(nextRecord ?? []);
        const forward = passThroughProps.onTextChange as
            | ((event: unknown, nextRecord: ICropRecordRow[]) => void)
            | undefined;
        if (forward) {
            forward(event, nextRecord);
        }
    };

    const forwardedOptions = {
        ...(passThroughProps[optionsPropName] as Record<string, unknown>),
        [PROPS_CROP_NAME]: selectableCropOptions,
    };

    return (
        <GridComponent
            {...passThroughProps}
            {...{ [optionsPropName]: forwardedOptions }}
            formatMessage={formatMessage}
            record={record}
            onTextChange={onTextChange}
            unlinkedCropGuids={unlinkedCropGuids}
        />
    );
};

export default RestrictedCropList;
